import { supabase } from './supabase';
import {
  analyticalReadingPairs,
  analyticalToCanonical,
  buildAnalyticalBlocks,
  computeAnalyticalPointMetrics,
  defaultAnalyticalFamilyData,
  finiteOrNull,
  mergeAnalyticalFamilyData,
  resolveAnalyticalTemplate,
  suggestAnalyticalSetpoints,
  validateAnalyticalRowsForCalculation,
} from './analytical-calibration-profile';

export { getTodayDailyEnvironmentLog, upsertTodayDailyEnvironmentLog } from './api-pressure-manometer-pilot';

export const DEFAULT_ANALYTICAL_VALIDITY_MONTHS = 12;
const VALIDITY_DAYS_BY_MONTHS = { 3: 90, 6: 181, 12: 365, 24: 730, 36: 1095 };

function sanitizePatch(patch = {}) {
  return Object.fromEntries(Object.entries(patch).filter(([, value]) => value !== undefined && !(typeof value === 'number' && !Number.isFinite(value))));
}

async function currentUserId() {
  return (await supabase.auth.getUser())?.data?.user?.id ?? null;
}

export function resolveAnalyticalValidityDays(months = DEFAULT_ANALYTICAL_VALIDITY_MONTHS) {
  const value = Number(months);
  if (!Number.isFinite(value) || value <= 0) return VALIDITY_DAYS_BY_MONTHS[DEFAULT_ANALYTICAL_VALIDITY_MONTHS];
  return VALIDITY_DAYS_BY_MONTHS[value] ?? Math.round(365 / 12 * value);
}

export function addAnalyticalValidityDaysToIsoDate(isoDate, months = DEFAULT_ANALYTICAL_VALIDITY_MONTHS) {
  const base = String(isoDate || '').slice(0, 10);
  if (!base) return null;
  const date = new Date(`${base}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  date.setDate(date.getDate() + resolveAnalyticalValidityDays(months));
  return date.toISOString().slice(0, 10);
}

async function getTodayEnvironment() {
  const { getTodayDailyEnvironmentLog } = await import('./api-pressure-manometer-pilot');
  try { return await getTodayDailyEnvironmentLog(); } catch { return null; }
}

export async function createAnalyticalSession({
  instrumentId,
  serviceOrderId = null,
  serviceOrderCode = null,
  certificateType = 'NO_SAC',
  validityMonths = DEFAULT_ANALYTICAL_VALIDITY_MONTHS,
  templateCode = 'ANALYTICAL-PH:ph_meter:v1',
}) {
  if (!instrumentId) throw new Error('instrumentId es requerido');
  const template = resolveAnalyticalTemplate(templateCode);
  const { data: instrument, error: instrumentError } = await supabase
    .from('instruments')
    .select(`id, tag, lab_id, brand, model, serial, type, unit, resolution, range_min, range_max, tolerance,
             client:client_id ( id, client_number, name, address, locality, province )`)
    .eq('id', instrumentId)
    .single();
  if (instrumentError) throw instrumentError;

  let resolution;
  let rangeMin;
  let rangeMax;
  try {
    resolution = analyticalToCanonical(instrument?.resolution, instrument?.unit || template.unit, template.code);
    rangeMin = analyticalToCanonical(instrument?.range_min, instrument?.unit || template.unit, template.code);
    rangeMax = analyticalToCanonical(instrument?.range_max, instrument?.unit || template.unit, template.code);
  } catch (error) {
    throw new Error(`${error.message} Configurá una unidad compatible antes de iniciar la calibración.`);
  }

  const environment = await getTodayEnvironment();
  const familyData = mergeAnalyticalFamilyData({
    ...defaultAnalyticalFamilyData(template.code),
    template_code: template.code,
    original_instrument_unit: instrument?.unit || null,
    values_normalized_to: template.unit,
  }, template.code);
  const payload = {
    family: 'analytical', instrument_id: instrument.id, service_order_id: serviceOrderId,
    service_order_code: serviceOrderCode || null, certificate_type: certificateType, status: 'draft',
    template_code: template.code, template_revision: template.revision,
    client_number: instrument?.client?.client_number ? String(instrument.client.client_number) : null,
    client_name: instrument?.client?.name || null, client_address: instrument?.client?.address || null,
    client_city: instrument?.client?.locality || null, client_province: instrument?.client?.province || null,
    instrument_object: instrument?.type || template.objectFallback, instrument_brand: instrument?.brand || null,
    instrument_model: instrument?.model || null, instrument_serial: instrument?.serial || null,
    instrument_tag: instrument?.tag || null, instrument_unit: template.unit, instrument_resolution: resolution,
    instrument_range_min: rangeMin, instrument_range_max: rangeMax, instrument_class: finiteOrNull(instrument?.tolerance),
    standard_id: null, standard_name: null, standard_serial: null, standard_certificate: null,
    standard_calibration_date: null, ambient_pressure_avg: finiteOrNull(environment?.pressure_hpa),
    ambient_temp_avg: finiteOrNull(environment?.temp_c), ambient_humidity_avg: finiteOrNull(environment?.humidity_rh),
    validity_months: Number(validityMonths) || DEFAULT_ANALYTICAL_VALIDITY_MONTHS,
    family_data: familyData, created_by: await currentUserId(),
  };
  const { data: session, error } = await supabase.from('calibration_sessions').insert(payload).select('*').single();
  if (error) throw error;
  const blocks = buildAnalyticalBlocks(template.code).map((item) => ({ ...item, resolution, session_id: session.id }));
  const { error: blockError } = await supabase.from('calibration_session_blocks').insert(blocks);
  if (blockError) throw blockError;
  return session;
}

export async function listAnalyticalStandardOptions({ templateCode = 'ANALYTICAL-PH:ph_meter:v1', onDate } = {}) {
  const { data, error } = await supabase.rpc('list_analytical_standard_options', {
    p_template_code: templateCode,
    p_on_date: onDate || new Date().toISOString().slice(0, 10),
  });
  if (error) throw error;
  return data || [];
}

export async function validateAnalyticalStandardConfiguration(sessionId) {
  const { data, error } = await supabase.rpc('validate_analytical_session_standards', { p_session_id: sessionId });
  if (error) throw error;
  return data || { valid: false, issues: ['No se pudo validar la trazabilidad.'], configuration: { references: [] } };
}

export async function validateAnalyticalProfileData(sessionId) {
  const { data, error } = await supabase.rpc('validate_analytical_profile_data', { p_session_id: sessionId });
  if (error) throw error;
  return data || { valid: false, issues: ['No se pudo validar el perfil analítico.'] };
}

export async function configureAnalyticalSessionStandards(sessionId, capabilityIds = []) {
  const { data, error } = await supabase.rpc('configure_analytical_session_standards', {
    p_session_id: sessionId,
    p_capability_ids: capabilityIds.filter(Boolean),
  });
  if (error) throw error;
  return data;
}

export async function getAnalyticalSession(sessionId) {
  const { data, error } = await supabase.from('calibration_sessions').select('*').eq('id', sessionId).eq('family', 'analytical').single();
  if (error) throw error;
  return data;
}

export async function updateAnalyticalSession(sessionId, patch) {
  const normalized = sanitizePatch(patch);
  if (normalized.family_data) {
    const { data: current } = await supabase.from('calibration_sessions').select('template_code').eq('id', sessionId).maybeSingle();
    normalized.family_data = mergeAnalyticalFamilyData(normalized.family_data, current?.template_code);
  }
  const { data, error } = await supabase.from('calibration_sessions').update({
    ...normalized, updated_at: new Date().toISOString(), updated_by: await currentUserId(),
  }).eq('id', sessionId).eq('family', 'analytical').select('*').single();
  if (error) throw error;
  return data;
}

export async function listAnalyticalSessionsByInstrument(instrumentId, templateCode = null) {
  let query = supabase.from('calibration_sessions').select('*').eq('instrument_id', instrumentId).eq('family', 'analytical');
  if (templateCode) query = query.eq('template_code', templateCode);
  const { data, error } = await query.order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function listAnalyticalBlocks(sessionId) {
  const { data, error } = await supabase.from('calibration_session_blocks').select('*').eq('session_id', sessionId).order('sort_order');
  if (error) throw error;
  return data || [];
}

export async function listAnalyticalPoints(sessionId) {
  const { data, error } = await supabase.from('calibration_session_points').select('*').eq('session_id', sessionId).order('point_no').order('series_no');
  if (error) throw error;
  return data || [];
}

export function analyticalPointsToRows(block, points = [], session = {}) {
  const template = resolveAnalyticalTemplate(session.template_code);
  const pointCount = Number(block?.point_count) || template.pointCount;
  const suggested = suggestAnalyticalSetpoints(session.instrument_range_min, session.instrument_range_max, template.code);
  const pairs = analyticalReadingPairs(template.code);
  const rows = Array.from({ length: pointCount }, (_, index) => {
    const setpoint = suggested[index] ?? template.defaultSetpoints[index] ?? '';
    const row = { point_no: index + 1, setpoint };
    pairs.forEach((pair) => { row[pair.patternField] = setpoint; row[pair.instrumentField] = ''; });
    return row;
  });
  points.forEach((point) => {
    if (block?.id && point.block_id !== block.id) return;
    const row = rows[Number(point.point_no) - 1];
    if (!row) return;
    const pair = pairs.find((candidate) => candidate.seriesNo === Number(point.series_no));
    if (!pair) return;
    row.setpoint = point.setpoint ?? row.setpoint;
    row[pair.patternField] = point.pattern_reading ?? '';
    row[pair.instrumentField] = point.instrument_reading ?? '';
  });
  return rows;
}

export async function saveAnalyticalPoints(sessionId, block, rows = [], templateCode) {
  if (!sessionId || !block?.id) throw new Error('Sesión y bloque requeridos');
  const template = resolveAnalyticalTemplate(templateCode);
  const pairs = analyticalReadingPairs(template.code);
  const payload = [];
  rows.forEach((row) => {
    const pointNo = Number(row.point_no);
    if (!Number.isFinite(pointNo)) return;
    const setpoint = finiteOrNull(row.setpoint);
    pairs.forEach((pair) => {
      const pattern = finiteOrNull(row[pair.patternField]);
      const instrument = finiteOrNull(row[pair.instrumentField]);
      if (pattern === null || instrument === null) return;
      payload.push({
        session_id: sessionId, block_id: block.id, point_no: pointNo, series_no: pair.seriesNo,
        direction: 'NA', setpoint: setpoint ?? pattern, pattern_reading: pattern,
        instrument_reading: instrument, unit: template.unit,
      });
    });
  });
  const { error: deleteError } = await supabase.from('calibration_session_points').delete().eq('session_id', sessionId).eq('block_id', block.id);
  if (deleteError) throw deleteError;
  if (!payload.length) return [];
  const { data, error } = await supabase.from('calibration_session_points').insert(payload).select('*');
  if (error) throw error;
  return data || [];
}

export async function calculateAnalyticalResults(sessionId) {
  const [standardValidation, profileValidation] = await Promise.all([
    validateAnalyticalStandardConfiguration(sessionId), validateAnalyticalProfileData(sessionId),
  ]);
  if (!standardValidation?.valid) throw new Error((standardValidation?.issues || ['La configuración de referencias no es válida.']).join(' '));
  if (!profileValidation?.valid) throw new Error((profileValidation?.issues || ['La configuración del perfil no es válida.']).join(' '));
  const [session, blocks, points] = await Promise.all([
    getAnalyticalSession(sessionId), listAnalyticalBlocks(sessionId), listAnalyticalPoints(sessionId),
  ]);
  const effectiveSession = {
    ...session,
    family_data: mergeAnalyticalFamilyData({
      ...session.family_data, standard_configuration: standardValidation.configuration || {},
    }, session.template_code),
  };
  const results = [];
  blocks.forEach((block) => {
    const rows = analyticalPointsToRows(block, points, effectiveSession);
    const rowValidation = validateAnalyticalRowsForCalculation(rows, effectiveSession);
    if (!rowValidation.valid) throw new Error(`${block.display_name}: ${rowValidation.issues.join(' ')}`);
    rows.forEach((row) => {
      const metrics = computeAnalyticalPointMetrics(row, effectiveSession);
      if (metrics?.traceability_issue) throw new Error(`${block.display_name} · punto ${row.point_no}: ${metrics.traceability_issue}`);
      if (metrics) results.push({ session_id: sessionId, block_id: block.id, point_no: row.point_no, ...metrics });
    });
  });
  if (!results.length) throw new Error('Completá al menos un punto con lecturas del material de referencia y del instrumento.');
  const { error: deleteError } = await supabase.from('calibration_session_results').delete().eq('session_id', sessionId);
  if (deleteError) throw deleteError;
  const { error } = await supabase.from('calibration_session_results').insert(results);
  if (error) throw error;
  await updateAnalyticalSession(sessionId, { status: 'calculated', family_data: effectiveSession.family_data });
  return results;
}

export async function listAnalyticalResults(sessionId) {
  const { data, error } = await supabase.from('calibration_session_results').select('*').eq('session_id', sessionId).order('block_id').order('point_no');
  if (error) throw error;
  return data || [];
}
